import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { OrganizationAccessPermissionsService } from '@connect-services/client/services/organization-access-permissions.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { WarehouseAndFormsAccessPermissionsService } from '@connect-services/client/services/warehouse-and-forms-access-permissions.service';
import { environment } from '@connect-services/src/environments/environment';
import { Role } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { EnvironmentNameEnum } from '@shared/client/models/environment.interface';

class ManagementPanel {
    public label!: string;
    public value!: RoutePathEnum;
}

export enum ManagementPanelEnum {
    FORM = 'form',
    NOTIFICATION = 'notification',
    NO_PERMISSIONS = 'insufficientPermissions',
    PASSWORDS = 'password',
    REFERENCE_DATA = 'referenceData',
    ROLES = 'role',
    USERS = 'user',
    USERS_AND_ROLES = 'usersAndRoles',
    WAREHOUSE = 'warehouse',
}

export enum ManagementSectionEnum {
    DATA = 'data',
    ORGANIZATION = 'organization',
}

const dataRoutes: RoutePathEnum[] = [
    RoutePathEnum.FORMS,
    RoutePathEnum.REFERENCE_DATA,
    RoutePathEnum.ORGANIZATION_DATA,
];

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-management-panel',
    styleUrl: './management-panel.component.scss',
    templateUrl: './management-panel.component.html',
})
export class ManagementPanelComponent {
    public activePanel: RoutePathEnum | null = null;
    public dataPanel: ManagementPanel[] = [
        {
            label: ManagementPanelEnum.WAREHOUSE,
            value: RoutePathEnum.ORGANIZATION_DATA,
        },
        {
            label: ManagementPanelEnum.FORM,
            value: RoutePathEnum.FORMS,
        },
    ];
    public organizationPanel: ManagementPanel[] = [
        {
            label: ManagementPanelEnum.USERS_AND_ROLES,
            value: RoutePathEnum.USERS_ROLES,
        },
        {
            label: 'Notification',
            value: RoutePathEnum.NOTIFICATION,
        },
        {
            label: ManagementPanelEnum.PASSWORDS,
            value: RoutePathEnum.PASSWORD_RULES,
        },
    ];
    public securityService = inject(SecurityService);

    protected readonly accessOrganizationPermissions = computed(() => {
        return {
            canAccessOrganization: this.organizationAccessPermissionsService.canAccessOrganization(),
            label: this.organizationAccessPermissionsService.usersRolesLabelKey(),
        };
    });
    protected readonly managementSection = signal<ManagementSectionEnum>(ManagementSectionEnum.DATA);

    protected readonly ManagementSectionEnum = ManagementSectionEnum;
    protected readonly RoutePathEnum = RoutePathEnum;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly organizationAccessPermissionsService = inject(OrganizationAccessPermissionsService);
    private readonly router = inject(Router);
    private readonly warehouseAndFormsAccessPermissionsService = inject(WarehouseAndFormsAccessPermissionsService);

    public constructor() {
        this.initializeDataPanel();
        this.getManagementPanelFromUrl();
        this.activePanel = this.getActivePanelFromUrl();
    }

    public getPanelLabelKey(panel: ManagementPanel): string {
        if (panel.value !== RoutePathEnum.USERS_ROLES) {
            return panel.label;
        }

        return this.accessOrganizationPermissions().label;
    }

    public isUserAllowed(routePath: RoutePathEnum): boolean {
        const role: Role | null | undefined = this.securityService.meSignal()?.role;

        if (role?.isAdmin) {
            return true;
        }

        if (dataRoutes.includes(routePath)) {
            return this.canAccessDataPanel(routePath);
        }

        if (routePath === RoutePathEnum.USERS_ROLES) {
            return this.accessOrganizationPermissions().canAccessOrganization;
        }

        return false;
    }

    public selectMainPanel(value: RoutePathEnum): void {
        this.activePanel = value;
        this.router.navigate([value], { relativeTo: this.activatedRoute });
    }

    private canAccessDataPanel(routePath: RoutePathEnum): boolean {
        if (routePath === RoutePathEnum.ORGANIZATION_DATA || routePath === RoutePathEnum.REFERENCE_DATA) {
            return this.warehouseAndFormsAccessPermissionsService.canAccessWarehouse();
        }

        if (routePath === RoutePathEnum.FORMS) {
            return this.warehouseAndFormsAccessPermissionsService.canAccessForms();
        }

        return false;
    }

    private getActivePanelFromUrl(): RoutePathEnum | null {
        const url: string = this.router.url;

        if (url.includes(`/${RoutePathEnum.MANAGEMENT_DATA}/${RoutePathEnum.ORGANIZATION_DATA}`)) {
            return RoutePathEnum.ORGANIZATION_DATA;
        }

        if (url.includes(`/${RoutePathEnum.MANAGEMENT_DATA}/${RoutePathEnum.REFERENCE_DATA}`)) {
            return RoutePathEnum.REFERENCE_DATA;
        }

        if (url.includes(`/${RoutePathEnum.MANAGEMENT_DATA}/${RoutePathEnum.FORMS}`)) {
            return RoutePathEnum.FORMS;
        }

        if (url.includes(`/${RoutePathEnum.MANAGEMENT_ORGANIZATION}/${RoutePathEnum.USERS_ROLES}`)) {
            return RoutePathEnum.USERS_ROLES;
        }

        if (url.includes(`/${RoutePathEnum.MANAGEMENT_ORGANIZATION}/${RoutePathEnum.NOTIFICATION}`)) {
            return RoutePathEnum.NOTIFICATION;
        }

        return null;
    }

    private getManagementPanelFromUrl(): ManagementSectionEnum {
        const currentUrl: string = this.router.url;

        switch (true) {
            case currentUrl.includes(RoutePathEnum.MANAGEMENT_DATA):
                this.managementSection.set(ManagementSectionEnum.DATA);

                return ManagementSectionEnum.DATA;

            case currentUrl.includes(RoutePathEnum.MANAGEMENT_ORGANIZATION):
                this.managementSection.set(ManagementSectionEnum.ORGANIZATION);

                return ManagementSectionEnum.ORGANIZATION;

            default:
                return ManagementSectionEnum.DATA;
        }
    }

    private initializeDataPanel(): void {
        if (environment.env === EnvironmentNameEnum.PROD) {
            return;
        }

        this.dataPanel.splice(1, 0, {
            label: ManagementPanelEnum.REFERENCE_DATA,
            value: RoutePathEnum.REFERENCE_DATA,
        });
    }
}
